import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOptimizedImageUrl } from '../lib/images';

export default function ShortformContentPage() {
  useEffect(() => {
    document.title = 'Strategisk Shortform Content - Health4media.dk';
    window.scrollTo(0, 0);
  }, []);

  const pillars = [
    {
      number: '01',
      title: 'Sundhedsfaglig troværdighed',
      desc: 'Respekt for lægefaglig etik uden støj og salgstricks.'
    },
    {
      number: '02',
      title: 'Gør det svære letforståeligt',
      desc: 'Komplekse diagnoser forklaret på klart og roligt dansk.'
    },
    {
      number: '03',
      title: 'Hurtig tillidsopbygning',
      desc: 'Patienten mærker empatien og roen inden første booking.'
    },
    {
      number: '04',
      title: 'Mobil- og algoritmeoptimeret',
      desc: '9:16-video med tydelige undertekster og præcis klipning.'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HERO SECTION */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4 sm:space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs sm:text-sm font-semibold tracking-wide backdrop-blur-xs mb-3">
                <i className="fa-solid fa-shield-halved text-xs" aria-hidden="true" />
                <span>0 måneders binding · Fuld frihed</span>
              </div>

              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Shortform video, der skaber tillid, synlighed & flere patienter
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed font-normal">
                Patienter vælger den behandler, de føler sig trygge ved. Vi forvandler jeres smartphone-optagelser til fængende, professionelle sundhedsvideoer med undertekster og grafik, der opbygger stærk patienttillid, sikrer overlegen lokal synlighed og tiltrækker nye patienter.
              </p>
              <div className="pt-2 sm:pt-4 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 sm:px-7 sm:py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm sm:text-base transition shadow-lg shadow-sky-950/40"
                >
                  <span>Book en samtale</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-full bg-transparent hover:bg-transparent hover:-translate-y-1 transform text-white font-medium text-xs sm:text-sm transition-all duration-200 border border-white"
                >
                  <span>Se eksempler & cases</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-4/3 shadow-2xl border border-slate-700 bg-slate-800 max-h-[260px] sm:max-h-none">
                <img
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/shortform-phone-filming.png", { width: 700, height: 525, fit: 'cover' })}
                  alt="Strategisk shortform videoproduktion i sundhedsklinik"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* WHY SHORTFORM FOR HEALTHCARE */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Hvorfor virker shortform video så stærkt i sundhedssektoren?
            </h2>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">
              Når patienter søger hjælp til smerter, operationer, genoptræning eller tandbehandling, handler valget af klinik om tryghed. En kort video fjerner usikkerhed langt hurtigere end traditionelle teksttunge hjemmesider.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {pillars.map((pillar, idx) => (
              <div
                key={idx}
                className="bg-slate-50 p-6 sm:p-7 rounded-2xl border border-slate-200 flex flex-col justify-between"
              >
                <div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-sky-700 tracking-tight block mb-2">
                    {pillar.number}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {pillar.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* WHAT IS INCLUDED */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Hvad indeholder forløbet for videoredigering & content?
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                I behøver ikke være tekniske eksperter eller bruge tid på redigeringsprogrammer. I filmer nemt selv efter vores råd – og vi klarer alt redigerings- og opsætningsarbejdet.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Online idé- & emnesparring</h4>
                    <p className="text-xs sm:text-sm text-slate-600">Vi hjælper med at udvælge emner og konkrete spørgsmål, patienterne søger svar på.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">I optager selv med mobilen</h4>
                    <p className="text-xs sm:text-sm text-slate-600">I skyder optagelserne i klinikken, når det passer jer, og uploader nemt råfilerne til os.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Fuld professionel redigering</h4>
                    <p className="text-xs sm:text-sm text-slate-600">Vi klipper videoerne, renser lyden, tilføjer tydelige undertekster og jeres klinikprofil.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-sky-600 text-sm mt-1 shrink-0" aria-hidden="true" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Meta postkalender & kanalklargøring</h4>
                    <p className="text-xs sm:text-sm text-slate-600">Leveres klar til Instagram Reels, TikTok, Shorts og Facebook – eller sat direkte op i jeres Meta kalender.</p>
                  </div>
                </div>
              </div>

            </div>

            <div className="bg-slate-900 rounded-3xl p-8 sm:p-10 text-white border border-slate-800 space-y-6">
              <h3 className="text-2xl font-bold text-white tracking-tight">
                Etik og patientsikkerhed i højsædet
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Vi har dyb respekt for reglerne omkring sundhedskommunikation i Danmark. Vi sikrer, at jeres videoer overholder gældende retningslinjer, bevarer klinikkens gode omdømme og altid sætter patientens tarv først.
              </p>
              <div className="pt-4 border-t border-slate-800">
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition"
                >
                  <span>Book en samtale om shortform video</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
